/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import React, { useMemo, useState } from "react";
import { FaUnlock } from "react-icons/fa";
import { GrUndo } from "react-icons/gr";
import { MdBlock } from "react-icons/md";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import AddNoteIcon from "../../../public/icons/popOver/AddNoteIcon";
import ApplyRemoveLabelsIcon from "../../../public/icons/popOver/ApplyRemoveLabels";
import ApplyRemoveTagsIcon from "../../../public/icons/popOver/ApplyRemoveTags";
import UnsubscribeIcon from "../../../public/icons/popOver/UnsubscribeIcon";
import { ContactApi } from "../../api/Contacts";
import {
  AllContactsTotalAtom,
  ContactFilterAtom,
  IsCheckedAllContactsAtom,
} from "../../atoms/ContactFilterAtom";
import { ContactFiltersAtom } from "../../atoms/ContactFiltersAtom";
import { ContactsAtom } from "../../atoms/ContactsAtom";
import {
  ExcludeContactIdsAtom,
  SelectedContactsAtom,
} from "../../atoms/SelectedContactsAtom";
import { TagsAtom } from "../../atoms/TagsAtom";
import { UpdateContactsFlagAtom } from "../../atoms/UpdateContactsAtom";
import { UserAtom } from "../../atoms/UserAtom";
import { ContactStatus, ICategoryType } from "../../generated/Api";
import useClickOutside from "../../hook/useClickOutside";
import { customTooltip, notSelect } from "../../styles/general";
import {
  CONTACT_STATUS_TO_RECEIVE_CONFIRMATION,
  IContact,
} from "../../types/Contact";
import { ITag } from "../../types/TagTypes";
import { generateCSV } from "../../utils/CreateCSVFromContacts";
import Icon from "../Icon";
import BulkAddOrRemoveLabel from "../Label/BulkAddOrRemoveLabel";
import CreateUserNote from "../Modal/container/CreateUserNote";
import AddOrRemoveTagModal from "../Modal/container/Tags/BulkAddOrRemove";
import { UnsuppressContactModal } from "../Modal/container/UnsuppressContact";
import Warning from "../Modal/container/Warning";
import { useModal } from "../Modal/Modal";
import StyledButton from "../StyledButton";
import BlockContactModal from "./BlockContactModal";

const Container = styled.div`
  position: relative;
  color: #2f2f2f;
  ${notSelect}
  height: 40px;
  width: 40px;
  display: grid;
  place-items: center;
`;

const Popover = styled.div`
  position: absolute;
  background: white;
  z-index: 1002;
  top: 40px;
  height: fit-content;
  width: 230px;
  overflow: auto;
  border-radius: 5px;
  box-shadow: 0px 3px 6px #00000026;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  padding: 10px;

  svg,
  img {
    flex-shrink: 0;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: #2f2f2f;
  cursor: pointer;

  span {
    margin-left: 7px;
  }

  svg {
    fill: #bbb;
  }

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
    border-radius: 5px;

    svg {
      fill: #fff;
    }
  }
`;

const Divider = styled.div`
  border-top: 1px solid #eaeaea;
  margin: 10px -10px;
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const Title = styled.div`
  color: #888888;
  font-size: 14px;
  padding: 10px 15px;
`;

const pathHover = css`
  :hover {
    svg {
      path {
        fill: #fff;
      }
    }
  }
`;

const tagHover = css`
  &:hover {
    svg {
      g {
        path {
          stroke: white;
        }
        #Group_113 {
          path {
            stroke: white;
            fill: white;
          }
        }
        circle {
          fill: #bbb;
          stroke: white;
        }
      }
    }
  }
`;

interface IActionsPopOver {
  setLoadingBlock: (value: boolean) => void;
}

export default function ActionsPopOver(props: IActionsPopOver): JSX.Element {
  const { setLoadingBlock } = props;

  const [openDropView, setOpenDropView] = useState<boolean>(false);
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const [contacts, setContacts] = useRecoilState(ContactsAtom);
  const [contactFilter, setContactFilter] = useRecoilState(ContactFilterAtom);
  const total = useRecoilValue(AllContactsTotalAtom);
  const contactFilters = useRecoilValue(ContactFiltersAtom);
  const excludeContactIds = useRecoilValue(ExcludeContactIdsAtom);
  const selectAll = useRecoilValue(IsCheckedAllContactsAtom);
  const [selectedContacts, setSelectedContacts] =
    useRecoilState(SelectedContactsAtom);
  const user = useRecoilValue(UserAtom);
  const tags = useRecoilValue<ITag[]>(TagsAtom);
  const setUpdateContactsFlag = useSetRecoilState(UpdateContactsFlagAtom);

  const modal = useModal();

  const hasASuppressedContact = useMemo(() => {
    return selectedContacts.find(
      (cId) =>
        contacts.find((c) => c._id === cId)?.status === ContactStatus.SUPRESSED
    );
  }, [selectedContacts, contacts]);

  const hasANonAdminUnsubscribedContact = useMemo(() => {
    return selectedContacts.find(
      (cId) =>
        contacts.find((c) => c._id === cId)?.status !==
        ContactStatus.ADMIN_UNSUBSCRIBED
    );
  }, [selectedContacts, contacts]);

  return (
    <Container ref={wrapperRef}>
      <div
        style={{ cursor: "pointer" }}
        data-tip
        data-for="actions"
        onClick={() => setOpenDropView(!openDropView)}
      >
        <ReactTooltip
          id="actions"
          place="bottom"
          effect="solid"
          globalEventOff="click"
          backgroundColor="#333333"
          css={customTooltip}
        >
          <TooltipText>More Actions</TooltipText>
        </ReactTooltip>
        <StyledButton
          css={css`
            height: 40px;
            width: 40px;
          `}
          inverted
          onClick={() => setOpenDropView(!openDropView)}
          role="button"
          tabIndex={0}
        >
          <Icon name="threedots" />
        </StyledButton>
      </div>

      {openDropView && (
        <Popover>
          {selectedContacts.length > 0 && (
            <>
              <Title>BULK ACTIONS</Title>

              <Row
                css={pathHover}
                onClick={async () => {
                  if (!contacts) return;

                  let csv: string;
                  setLoadingBlock(true);
                  if (selectAll) {
                    csv = (await generateCSV({
                      tags,
                      query: {
                        ...contactFilters,
                        tagQuery: contactFilter?.tagQuery,
                      },
                    })) as string;
                  } else {
                    csv = (await generateCSV({
                      tags,
                      contacts,
                      selectedContacts,
                    })) as string;
                  }

                  setLoadingBlock(false);

                  let now = dayjs();

                  let downloadLink = document.createElement("a");

                  const csvDataArray = [];
                  csvDataArray.push(csv);

                  const url =
                    process.browser &&
                    window.URL.createObjectURL(
                      new Blob(csvDataArray, { type: "application/csv" })
                    );

                  downloadLink.href = url;
                  downloadLink.download = `${now.format(
                    "YYYY.MM.DD_HHmm"
                  )}_berserkermail_export.csv`;

                  document.body.appendChild(downloadLink);
                  downloadLink.click();
                  process.browser && window.URL.revokeObjectURL(url);
                  document.body.removeChild(downloadLink);
                }}
              >
                <Icon name="csv" width={16} height={16} />
                <span>Export list to CSV</span>
              </Row>

              <Row
                css={css`
                  svg {
                    color: #bbb;
                  }
                  :hover {
                    path {
                      fill: white !important;
                    }
                  }
                `}
                onClick={async () => {
                  try {
                    if (selectAll) {
                      modal.pushModal(
                        <AddOrRemoveTagModal
                          setContacts={setContacts}
                          selectAll={selectAll}
                          contactFilter={contactFilter}
                          query={contactFilters}
                          excludeContactIds={excludeContactIds}
                        />,
                        "Apply or Remove Tags"
                      );
                    } else {
                      modal.pushModal(
                        <AddOrRemoveTagModal
                          setContacts={setContacts}
                          selectedContactIds={selectedContacts}
                        />,
                        "Apply or Remove Tags"
                      );
                    }
                  } catch (err) {
                    console.log({ err });
                  }
                }}
              >
                {/* <Icon name="apply" width={16} height={16} /> */}
                <ApplyRemoveTagsIcon />
                <span
                  css={css`
                    width: 250px;
                  `}
                >
                  Apply or remove tags
                </span>
              </Row>
              <Row
                css={css`
                  svg {
                    color: #bbb;
                  }
                  :hover {
                    path {
                      fill: white !important;
                    }
                  }
                `}
                onClick={async () => {
                  try {
                    modal.pushModal(
                      <BulkAddOrRemoveLabel
                        query={
                          {
                            ...contactFilters,
                            tagQuery: contactFilter?.tagQuery,
                            userUid: user.userUid,
                          } as any
                        }
                        contacts={selectAll ? [] : selectedContacts}
                        setLoadingBlock={setLoadingBlock}
                      />,
                      "Apply or Remove Labels"
                    );
                  } catch (err) {
                    console.log({ err });
                  }
                }}
              >
                {/* <Icon name="assignlabelgrey" width={15} height={15} /> */}
                <ApplyRemoveLabelsIcon />
                <span
                  css={css`
                    width: 250px;
                  `}
                >
                  Apply or remove labels
                </span>
              </Row>

              <Row
                css={css`
                  svg {
                    color: #bbb;
                  }
                  :hover {
                    path {
                      fill: white !important;
                    }
                  }
                `}
                onClick={async () => {
                  try {
                    modal.pushModal(
                      <CreateUserNote
                        contactIds={selectedContacts}
                        selectAll={selectAll}
                      />,
                      "User note"
                    );
                  } catch (err) {
                    console.log({ err });
                  }
                }}
              >
                {/* <Icon name="apply" width={16} height={16} /> */}
                <AddNoteIcon />
                <span
                  css={css`
                    width: 250px;
                  `}
                >
                  Add a note
                </span>
              </Row>

              <Row
                css={pathHover}
                onClick={async () => {
                  try {
                    let response;
                    let contactsToResendConfirmationIds = [];
                    let status = CONTACT_STATUS_TO_RECEIVE_CONFIRMATION;

                    if (selectAll) {
                      toast("Sending confirmation emails...", {
                        type: "success",
                      });

                      status = contactFilters?.status?.length
                        ? (status as ContactStatus[]).filter((s) =>
                            CONTACT_STATUS_TO_RECEIVE_CONFIRMATION.includes(s)
                          )
                        : CONTACT_STATUS_TO_RECEIVE_CONFIRMATION;

                      response = await ContactApi.sendConfirmation({
                        query: {
                          ...contactFilters,
                          tagQuery: contactFilter?.tagQuery,
                        },
                      });
                    } else {
                      const filteredContacts = contacts.filter((contact) =>
                        selectedContacts.includes(contact._id)
                      );

                      contactsToResendConfirmationIds = filteredContacts
                        .filter((c) => status.includes(c.status))
                        .map((c) => c._id);

                      response = await ContactApi.sendConfirmation({
                        contactIds: contactsToResendConfirmationIds,
                      });
                    }

                    if (response) {
                      let contactsToUpdate = contacts.filter((c) =>
                        status.includes(c.status)
                      );

                      if (selectAll)
                        contactsToUpdate = contactsToUpdate.map((c) => ({
                          ...c,
                          status: ContactStatus.PENDING_CONFIRMATION,
                        }));
                      else
                        contactsToUpdate = contactsToUpdate.map((c) =>
                          contactsToResendConfirmationIds.includes(c._id)
                            ? {
                                ...c,
                                status: ContactStatus.PENDING_CONFIRMATION,
                              }
                            : c
                        );

                      setContacts((c) => {
                        return c.map((contact) => {
                          const contactFromArray = contactsToUpdate.find(
                            (c) => c._id === contact._id
                          );
                          return contactFromArray || contact;
                        });
                      });

                      toast("Email confirmation sent to selected contacts", {
                        type: "success",
                      });
                    }
                  } catch (e) {
                    console.error(e);
                  }
                }}
              >
                <Icon name="resend" width={16} height={16} />
                <span>Send email confirmation</span>
              </Row>

              <Row
                css={css`
                  svg {
                    color: #bbb;
                  }
                  :hover {
                    path {
                      fill: white !important;
                    }
                  }
                `}
                onClick={() => {
                  {
                    hasANonAdminUnsubscribedContact
                      ? modal.pushModal(
                          <Warning
                            icon={"./icons/emailReport/nounsubscribe.svg"}
                            title="Unsubscribe"
                            description={`Are you sure you want unsubscribe the ${
                              selectAll ? total : selectedContacts.length
                            } ${
                              selectedContacts.length > 1
                                ? "contacts"
                                : "contact"
                            }?`}
                            buttonsName={["No", "Yes"]}
                            onOkClicked={async () => {
                              let response;
                              setLoadingBlock(true);
                              if (selectAll) {
                                let tagQuery = contactFilter?.tagQuery;
                                response =
                                  await ContactApi.manyAdminUnsubscribed({
                                    query: {
                                      ...contactFilters,
                                      tagQuery,
                                    },
                                  });
                              } else {
                                response =
                                  await ContactApi.manyAdminUnsubscribed({
                                    contactIds: selectedContacts,
                                  });
                              }

                              if (response) {
                                setContacts(
                                  (contacts) =>
                                    contacts &&
                                    contacts.map((c) => {
                                      if (selectedContacts.includes(c._id))
                                        return {
                                          ...c,
                                          status:
                                            ContactStatus.ADMIN_UNSUBSCRIBED,
                                        };
                                      else return c;
                                    })
                                );
                              }
                              setLoadingBlock(false);
                              modal.popModal();
                            }}
                            onCancelClicked={() => {
                              modal.popModal();
                            }}
                          />
                        )
                      : modal.pushModal(
                          <Warning
                            icon={"./icons/emailReport/nounsubscribe.svg"}
                            title="Resubscribe"
                            description={`Are you sure you want resubscribe the ${
                              selectAll ? total : selectedContacts.length
                            } unsubscribed ${
                              selectedContacts.length > 1
                                ? "contacts"
                                : "contact"
                            }?`}
                            buttonsName={["No", "Yes"]}
                            onOkClicked={async () => {
                              let response;
                              setLoadingBlock(true);
                              if (selectAll) {
                                let tagQuery = contactFilter?.tagQuery;
                                response =
                                  await ContactApi.manyRevertAdminUnsubscribe({
                                    query: {
                                      ...contactFilters,
                                      tagQuery,
                                    },
                                  });
                              } else {
                                response =
                                  await ContactApi.manyRevertAdminUnsubscribe({
                                    contactIds: selectedContacts,
                                  });
                              }

                              if (response) {
                                setContacts(
                                  (contacts) =>
                                    contacts &&
                                    contacts.map((c) => {
                                      if (selectedContacts.includes(c._id))
                                        return {
                                          ...c,
                                          status: ContactStatus.NOT_CONFIRMED,
                                        };
                                      else return c;
                                    })
                                );
                              }
                              setLoadingBlock(false);
                              modal.popModal();
                            }}
                            onCancelClicked={() => {
                              modal.popModal();
                            }}
                          />
                        );
                  }
                }}
              >
                {hasANonAdminUnsubscribedContact ? (
                  <>
                    <UnsubscribeIcon />
                    <span>Unsubscribe</span>
                  </>
                ) : (
                  <>
                    <GrUndo color="#70707080" />
                    <span>Resubscribe</span>
                  </>
                )}
              </Row>

              <Row
                css={tagHover}
                onClick={async () => {
                  if (selectAll) {
                    setLoadingBlock(true);
                    const allContacts = (
                      await ContactApi.read({
                        ...contactFilters,
                        tagQuery: contactFilter?.tagQuery,
                      })
                    ).contacts;
                    modal.pushModal(
                      <BlockContactModal
                        contacts={allContacts}
                        selectAll={selectAll}
                      />,
                      "Block contact"
                    );
                    setLoadingBlock(false);
                  } else {
                    modal.pushModal(
                      <BlockContactModal
                        contacts={contacts.filter((c) =>
                          selectedContacts.includes(c._id)
                        )}
                        selectAll={false}
                      />,
                      "Block contact"
                    );
                  }
                }}
              >
                <MdBlock />
                <span
                  css={css`
                    width: 250px;
                  `}
                >
                  Block
                </span>
              </Row>

              <Row
                css={pathHover}
                onClick={async () => {
                  let contactsToBeDeleted: IContact[] = [];
                  const userProptectedTags = user.options.protectedTags || [];

                  const filteredContacts = contacts.filter((contact) =>
                    selectedContacts.includes(contact._id)
                  );

                  const foundTestDriveContactTag = tags.find((t) => {
                    return (
                      t.name === "Testdrive Contact" &&
                      t.type === ICategoryType.System
                    );
                  });

                  contactsToBeDeleted = filteredContacts.filter(
                    (c) =>
                      !c.tags.find((tag) =>
                        userProptectedTags.includes(tag._id)
                      )
                  );

                  contactsToBeDeleted = contactsToBeDeleted.filter(
                    (c) =>
                      !c.tags.find(
                        (tag) => tag._id === foundTestDriveContactTag?._id
                      )
                  );

                  let contactsToBeDeletedCount = 0;
                  let totalContacts = 0;

                  if (!selectAll) {
                    contactsToBeDeletedCount = contactsToBeDeleted.length;
                    totalContacts = filteredContacts.length;
                  } else {
                    setLoadingBlock(true);
                    totalContacts = total;
                    contactsToBeDeletedCount = (
                      await ContactApi.checkDeleteCount({
                        query: {
                          ...contactFilters,
                          tagQuery: contactFilter?.tagQuery,
                        },
                      })
                    ).contatcsToBeDeletedCount;
                    setLoadingBlock(false);
                  }

                  if (contactsToBeDeletedCount) {
                    modal.pushModal(
                      <Warning
                        icon={"./icons/delete.svg"}
                        title="Delete Contacts"
                        description={
                          contactsToBeDeletedCount ===
                            selectedContacts.length ||
                          totalContacts === contactsToBeDeletedCount
                            ? `Are you sure you want to delete ${totalContacts} ${
                                selectedContacts.length > 1
                                  ? "contacts"
                                  : "contact"
                              }?`
                            : `${
                                totalContacts - contactsToBeDeletedCount
                              } of the contacts you have selected to delete are protected.\nDo you want to delete the other ${contactsToBeDeletedCount}?`
                        }
                        buttonsName={["No", "Yes"]}
                        onOkClicked={async () => {
                          try {
                            setLoadingBlock(true);
                            const deletedContactIds = contactsToBeDeleted.map(
                              (c) => c._id
                            );

                            let response;
                            if (selectAll) {
                              response = await ContactApi.removeMany({
                                query: {
                                  ...contactFilters,
                                  tagQuery: contactFilter?.tagQuery,
                                },
                              });
                            } else {
                              response = await ContactApi.removeMany({
                                contactIds: deletedContactIds,
                              });
                            }

                            if (response) {
                              setContacts((contacts) => {
                                if (!contacts) return [];

                                // Remove the contacts that were removed
                                contacts = contacts.filter(
                                  (c) => !deletedContactIds.includes(c._id)
                                );

                                return contacts;
                              });

                              setUpdateContactsFlag(true);

                              if (
                                contactsToBeDeletedCount ===
                                selectedContacts.length
                              ) {
                                setSelectedContacts([]);
                              }
                              setContactFilter(null);
                            }
                          } catch (e) {
                            console.error(e);
                          } finally {
                            setLoadingBlock(false);
                            modal.popModal();
                          }
                        }}
                        onCancelClicked={() => modal.popModal()}
                      />
                    );
                  } else {
                    modal.pushModal(
                      <Warning
                        icon={"./icons/delete.svg"}
                        title="Delete Contacts"
                        description={
                          "All these selected contacts have protection tags, so you cannot delete them."
                        }
                        buttonsName={["Cancel"]}
                        onOkClicked={() => {
                          modal.popModal();
                        }}
                        onCancelClicked={() => {
                          modal.popModal();
                        }}
                      />
                    );
                  }
                }}
              >
                <Icon name="delete" width={16} height={16} />
                <span>Delete</span>
              </Row>

              {hasASuppressedContact && (
                <Row
                  css={css`
                    :hover {
                      svg {
                        color: white !important;
                      }
                    }
                  `}
                  onClick={async () => {
                    try {
                      modal.pushModal(
                        <UnsuppressContactModal
                          suppressedContacts={selectedContacts
                            .map((cId) => contacts.find((c) => c._id === cId))
                            .filter((c) => c)}
                        />
                      );
                    } catch (err) {
                      console.log({ err });
                    }
                  }}
                >
                  <FaUnlock color="#70707080" />
                  <span
                    css={css`
                      width: 250px;
                    `}
                  >
                    Unsuppress suppressed contact(s)
                  </span>
                </Row>
              )}
            </>
          )}
        </Popover>
      )}
    </Container>
  );
}
